// PracticeForSession — position-aware interview practice.
//
// Replaces the old Practice.tsx loop that pulled from a global story bank
// and ignored the position. Pulls questions from this session's saved
// prep pack (or runner.log if it was generated this session and not yet
// persisted), categorizes them, and lets the user practice in a timed
// loop with:
//
//   - Streaming STAR+R answer generation (story_answer with company/role
//     context — not the old generic prompt)
//   - Voice mode: browser STT for user answer, browser TTS for bot
//     output, with paragraph-streaming overlap (borrowed from
//     IliaLarchenko/Interviewer's coding.py:87-104)
//   - Per-answer rubric feedback (grade_answer, streaming markdown)
//   - Adaptive follow-up: after each answer, optionally call
//     probe_followup which decides move-on vs probe; cap 1 probe per
//     anchor (FoloUp directive)
//   - Q/A pairs persisted to localStorage so Finish → recap can run
//     grade_session against the full transcript
//
// localStorage keys used:
//   nice2job.practice.{sessionId}              — PracticeSessionState
//   nice2job.job.story-answer-{sessionId}-{idx} — per-question answer runner
//   nice2job.job.grade-answer-{sessionId}-{idx} — per-question grader runner

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, ChevronLeft, ChevronRight, VolumeX, Star, Circle, Square, AlertTriangle } from 'lucide-react';
import Progress from '../ui/Progress';
import Chip from '../ui/Chip';
import { apiGet } from '../../lib/api';
import { useJobRunner } from '../../lib/useJobRunner';
import { cleanLLMOutput } from '../../lib/clean-llm-output';
import { parsePrepPack, countByCategory, type PrepCategory } from '../../lib/parse-prep-pack';
import { parseProbeJson } from '../../lib/parse-grade-json';
import { useVoiceLoop } from '../../lib/use-voice-loop';
import type { Application, PracticeSessionState, PracticeQA } from '../../lib/types';

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface PrepArtifact {
  file: string;
  kind: 'research' | 'prep' | 'other';
  company_slug: string;
  url: string;
  mtime: number;
}
interface PrepListResponse { total: number; items: PrepArtifact[] }

const CATEGORY_ORDER: PrepCategory[] = ['behavioral', 'technical', 'role-specific', 'red-flag'];
const CATEGORY_LABEL: Record<PrepCategory, string> = {
  behavioral: 'Behavioral',
  technical: 'Technical',
  'role-specific': 'Role-specific',
  'red-flag': 'Red flags',
};

function loadPracticeState(sessionId: string): PracticeSessionState {
  try {
    const raw = localStorage.getItem(`nice2job.practice.${sessionId}`);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return { sessionId, startedAt: Date.now(), qaPairs: [], notesByQuestion: {} };
}

function savePracticeState(state: PracticeSessionState) {
  try { localStorage.setItem(`nice2job.practice.${state.sessionId}`, JSON.stringify(state)); }
  catch { /* noop */ }
}

export default function PracticeForSession({
  session,
  sessionId,
  onSwitchToPrepTab,
}: {
  session: Application;
  sessionId: string;
  onSwitchToPrepTab: () => void;
}) {
  const nav = useNavigate();
  const slug = companySlug(session.company);

  // ── Load latest prep pack for this company ────────────────────────────
  const prepListQ = useQuery({
    queryKey: ['interview-prep-list', slug],
    queryFn: () => apiGet<PrepListResponse>(`/api/interview-prep/list?company_slug=${encodeURIComponent(slug)}`),
    staleTime: 30_000,
  });

  const latestPrep = useMemo(() => {
    const items = prepListQ.data?.items ?? [];
    const prepFiles = items.filter((i) => i.kind === 'prep' || i.kind === 'other');
    return prepFiles.sort((a, b) => b.mtime - a.mtime)[0] || null;
  }, [prepListQ.data]);

  const prepMdQ = useQuery({
    queryKey: ['prep-md', latestPrep?.url],
    queryFn: async () => {
      if (!latestPrep) return '';
      const res = await fetch(latestPrep.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    enabled: !!latestPrep,
    staleTime: 60_000,
  });

  // Fall back to in-progress prep runner if there is one (rare but useful
  // immediately after Generate-prep finishes — disk write race).
  const prepRunner = useJobRunner(`interview-prep-${sessionId}`);
  const fallbackPrepMd = prepRunner.status === 'done'
    ? cleanLLMOutput(prepRunner.log || '')
    : '';

  const allQuestions = useMemo(() => {
    const md = prepMdQ.data || fallbackPrepMd;
    return parsePrepPack(md);
  }, [prepMdQ.data, fallbackPrepMd]);

  // ── Filter chips ──────────────────────────────────────────────────────
  const [filter, setFilter] = useState<PrepCategory | 'all'>('all');
  const counts = useMemo(() => countByCategory(allQuestions), [allQuestions]);
  const filtered = useMemo(() =>
    filter === 'all' ? allQuestions : allQuestions.filter((q) => q.category === filter),
  [allQuestions, filter]);

  // ── Per-session persistent state ──────────────────────────────────────
  const [state, setState] = useState<PracticeSessionState>(() => loadPracticeState(sessionId));
  useEffect(() => { setState(loadPracticeState(sessionId)); }, [sessionId]);
  useEffect(() => { savePracticeState(state); }, [state]);

  // ── Cursor through filtered questions ─────────────────────────────────
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [filter]);
  const q = filtered[idx];

  // Reset answer/notes UI when navigating to a different question
  const [showAnswer, setShowAnswer] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // ── Multi-turn thread state (Phase 14d) ───────────────────────────────
  // When `activeQuestion` is null we're on the anchor (q.text). When the
  // user accepts a probe, activeQuestion holds the probe text and the
  // anchor's turn is committed to threadTurns. The conversation can
  // continue across many turns; user explicitly decides when to "Move on
  // to next question" via [Done & next].
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [threadTurns, setThreadTurns] = useState<PracticeQA[]>([]);
  const [pendingProbe, setPendingProbe] = useState<{ probe: string; reason: string } | null>(null);

  // Reset thread state on question change OR filter change.
  useEffect(() => {
    setActiveQuestion(null);
    setThreadTurns([]);
    setPendingProbe(null);
  }, [idx, filter]);

  // The question text actually shown — anchor by default, replaced by
  // probe text once user has accepted a probe.
  const displayQuestion = activeQuestion || q?.text || '';
  const turnIndex = threadTurns.length;        // 0 on anchor, N for the Nth probe

  // ── Recording state (merged Timer + Voice) ────────────────────────────
  // One control drives both: starts the 2-minute timer and (if browser
  // supports) the speech recognition. Stops both. UX is one big "Start
  // practicing" / "Stop" button instead of the old two-button design.
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [segments, setSegments] = useState(0); // count of final transcripts captured this take
  const timerRef = useRef<number | null>(null);
  const TIMER_LIMIT = 120; // 2 minutes — auto-stop here

  useEffect(() => {
    if (recording) {
      timerRef.current = window.setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          // Auto-stop at 2:00 — user can still see captured transcript + edit
          // notes, but the recording loop terminates cleanly so SR releases mic
          if (next >= TIMER_LIMIT) {
            setRecording(false);
            return TIMER_LIMIT;
          }
          return next;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [recording]);

  // Reset everything when navigating to a different question
  useEffect(() => {
    setElapsed(0);
    setSegments(0);
    setRecording(false);
  }, [idx, filter]);

  // ── Notes (per-turn, persisted) ───────────────────────────────────────
  // Notes are keyed by the displayed question text (anchor OR probe). The
  // functional updater form is essential here — onFinalUtterance fires ASYNC
  // from speech recognition and may stack multiple finals before React
  // re-renders. A plain `setNotes(notes + text)` would capture stale `notes`
  // and overwrite the previous segment.
  const noteKey = displayQuestion;
  const notes = (noteKey && state.notesByQuestion[noteKey]) || '';
  const setNotes = useCallback((next: string | ((cur: string) => string)) => {
    setState((s) => {
      if (!noteKey) return s;
      const cur = s.notesByQuestion[noteKey] || '';
      const resolved = typeof next === 'function' ? next(cur) : next;
      return { ...s, notesByQuestion: { ...s.notesByQuestion, [noteKey]: resolved } };
    });
  }, [noteKey]);

  // ── Per-turn runner keys ───────────────────────────────────────────────
  // Each turn (anchor + N probes) gets its own runner so re-drafting and
  // grading don't trample across turns. The key is stable while user is
  // working on a turn; bumping `turnIndex` flips us to fresh runners.
  const turnKey = `${sessionId}-${idx}-${filter}-t${turnIndex}`;

  // ── story_answer (LLM-drafted STAR+R answer, streaming) ───────────────
  const answerRunner = useJobRunner(`story-answer-${turnKey}`);
  const cleanAnswer = useMemo(() => cleanLLMOutput(answerRunner.log || ''), [answerRunner.log]);
  // Strip #NOTES# section from displayed answer (silent in voice mode too)
  const displayAnswer = useMemo(() => {
    const i = cleanAnswer.indexOf('#NOTES#');
    return i >= 0 ? cleanAnswer.slice(0, i).trim() : cleanAnswer;
  }, [cleanAnswer]);

  const startAnswer = () => {
    if (!q || answerRunner.isRunning) return;
    answerRunner.startJob('story_answer', {
      question: displayQuestion,           // the CURRENT turn question, not always anchor
      company: session.company,
      role: session.role,
      category: q.category,
      application_id: sessionId,
    });
    setShowAnswer(true);
  };

  // ── grade_answer (per-answer feedback, streaming markdown) ────────────
  const gradeRunner = useJobRunner(`grade-answer-${turnKey}`);
  const cleanGrade = useMemo(() => cleanLLMOutput(gradeRunner.log || ''), [gradeRunner.log]);
  const startGrade = () => {
    if (!q || gradeRunner.isRunning) return;
    const userAnswer = (notes || '').trim();
    // Button is disabled when notes is empty (`disabled={... || !notes.trim()}`),
    // so this is a defensive bail — no popup.
    if (!userAnswer) return;
    gradeRunner.startJob('grade_answer', {
      question: displayQuestion,
      answer: userAnswer,
      category: q.category,
      company: session.company,
      role: session.role,
    });
    setShowFeedback(true);
  };

  // ── probe_followup (adaptive follow-up decision, multi-turn) ──────────
  // No more 1-probe-per-anchor cap — user explicitly drives the loop with
  // [Probe deeper] and [Continue with this probe] / [Stop probing].
  const probeRunner = useJobRunner(`probe-${turnKey}`);
  const cleanProbe = useMemo(() => cleanLLMOutput(probeRunner.log || ''), [probeRunner.log]);
  const probeResult = useMemo(() => parseProbeJson(cleanProbe), [cleanProbe]);

  const triggerProbe = useCallback(() => {
    if (!q) return;
    const userAnswer = (notes || '').trim();
    // Just bail silently if there's nothing to probe on — the button is
    // already disabled in that state via `!notes.trim()`. The previous
    // length>=10 threshold was too aggressive (it'd alert on short answers
    // like "I led 8 people" because button enables at 1+ char) and the
    // probe LLM can decide on its own whether a short answer needs digging.
    if (!userAnswer) return;
    if (probeRunner.isRunning) return;
    // Build conversation history string from threadTurns + current turn
    // so the LLM sees the FULL trail. This is what makes follow-ups feel
    // contextual instead of repeating earlier questions.
    const history = [
      ...threadTurns.map((t) => `Q: ${t.question}\nA: ${t.answer}`),
      `Q: ${displayQuestion}\nA: ${userAnswer}`,
    ].join('\n\n');
    probeRunner.startJob('probe_followup', {
      anchor: q.text,                  // original prep-pack question for context
      answer: userAnswer,              // latest answer
      history,                          // full thread so far
      company: session.company,
      role: session.role,
      turn_index: turnIndex,            // helps backend bias toward move_on as turns pile up
    });
  }, [q, notes, probeRunner, threadTurns, displayQuestion, session, turnIndex]);

  // When probe_followup completes, surface the decision as pendingProbe so
  // the user can accept (continue thread) or skip (move to next question).
  useEffect(() => {
    if (probeRunner.status !== 'done') return;
    if (!probeResult) return;
    if (probeResult.move_on === false && probeResult.probe) {
      setPendingProbe({ probe: probeResult.probe, reason: probeResult.reason || '' });
    } else {
      // move_on:true — clear any prior pending probe; user sees a "✓ strong
      // answer" message inline (rendered separately from the banner)
      setPendingProbe(null);
    }
  }, [probeRunner.status, probeResult]);

  // Accept the pending probe → commit current Q/A to thread, swap question.
  const acceptProbe = useCallback(() => {
    if (!pendingProbe || !q) return;
    const userAnswer = (notes || '').trim();
    if (!userAnswer) return;
    const newTurn: PracticeQA = {
      question: displayQuestion,
      answer: userAnswer,
      category: q.category,
      ts: Date.now(),
      anchor: q.text,
      turn: turnIndex,
    };
    setThreadTurns((cur) => [...cur, newTurn]);
    setActiveQuestion(pendingProbe.probe);
    setPendingProbe(null);
    // Reset turn-local UI state; new turnKey will spin up fresh runners
    setShowAnswer(false);
    setShowFeedback(false);
    setSegments(0);
    setLastCaptured('');
    setRecording(false);
  }, [pendingProbe, q, notes, displayQuestion, turnIndex]);

  // Reject the pending probe (or the whole thread): finalize what we have
  // and move to the next anchor question.
  const rejectProbeAndAdvance = useCallback(() => {
    setPendingProbe(null);
    // We'll trigger the finalize-thread path via finalizeThread() below — but
    // since this can only be called when we're mid-thread or at the anchor
    // with a probe ready, we just reuse next(). next() flushes thread state.
    // (next/prev/finish defined further down call finalizeThread internally.)
    // No-op here; user has to click "Done & next" to truly advance — this
    // only dismisses the banner.
  }, []);

  // ── Voice loop wiring ─────────────────────────────────────────────────
  // Enabled exactly when the user is recording — STT, TTS overlap and
  // barge-in are all gated on this single boolean. No separate "voice mode"
  // toggle anymore. Default language follows navigator.language (set inside
  // the hook), so a Chinese-language browser gets zh-CN STT automatically.
  const [lastCaptured, setLastCaptured] = useState<string>('');
  const voice = useVoiceLoop({
    enabled: recording,
    onFinalUtterance: (text) => {
      // Functional setState — defends against the closure trap where two
      // finals fire back-to-back and the second sees stale `notes`.
      setNotes((cur) => (cur ? `${cur} ${text}` : text));
      setSegments((n) => n + 1);
      setLastCaptured(text);
    },
    onBargeIn: () => { /* no-op; voice loop already cancelled TTS */ },
  });

  // Auto-start STT when user clicks Record. Auto-stop when recording flips
  // off (manual stop OR timer expiry). The hook's enabled-flag effect also
  // calls stopListening on disable, but we kick start here so the hook
  // doesn't need an extra "auto-start" mode.
  useEffect(() => {
    if (recording) {
      if (voice.caps.canRecognize) voice.startListening();
    } else {
      voice.stopListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  // Stream LLM tokens to TTS in real time when recording is on.
  // CRITICAL: do NOT put `voice` in the deps array. The hook returns a fresh
  // object literal every render, so React would re-fire this effect on every
  // render — and the question-reset effect below would set showAnswer=false
  // on every render too, hiding the just-started Re-draft panel.
  const lastSpokenLenRef = useRef(0);
  useEffect(() => {
    if (!recording) return;
    if (!answerRunner.isActive) {
      lastSpokenLenRef.current = 0;
      return;
    }
    const text = cleanAnswer;
    if (text.length > lastSpokenLenRef.current) {
      const delta = text.slice(lastSpokenLenRef.current);
      voice.speakStreamPush(delta);
      lastSpokenLenRef.current = text.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, cleanAnswer, answerRunner.isActive]);

  // When changing question, reset spoken cursor + collapse answer/feedback panels.
  // Same reason: omit `voice` from deps so this only fires on actual question
  // navigation, not on every render.
  useEffect(() => {
    voice.speakStreamReset();
    lastSpokenLenRef.current = 0;
    setShowAnswer(false);
    setShowFeedback(false);
    setLastCaptured('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, filter]);

  // ── Finalize the current thread (anchor + all probes) into qaPairs ─────
  // Called when user navigates away from this anchor (Prev / Next / Finish).
  // Replaces any prior turns for this anchor (covers re-practice) so the
  // same prep-pack question doesn't double-count in the recap.
  const finalizeThread = useCallback(() => {
    if (!q) return;
    const userAnswer = (notes || '').trim();
    const allTurns: PracticeQA[] = [...threadTurns];
    if (userAnswer) {
      allTurns.push({
        question: displayQuestion,
        answer: userAnswer,
        category: q.category,
        ts: Date.now(),
        anchor: q.text,
        turn: turnIndex,
      });
    }
    setState((s) => {
      // Drop any existing rows whose anchor (or question, for legacy rows
      // without `anchor` field) matches this question — re-practice replaces.
      const cleaned = s.qaPairs.filter((p) => {
        const pAnchor = p.anchor || p.question;
        return pAnchor !== q.text;
      });
      return { ...s, qaPairs: [...cleaned, ...allTurns] };
    });
  }, [q, notes, threadTurns, displayQuestion, turnIndex]);

  const next = () => {
    finalizeThread();
    if (idx < filtered.length - 1) setIdx(idx + 1);
  };
  const prev = () => {
    finalizeThread();
    if (idx > 0) setIdx(idx - 1);
  };

  const finish = () => {
    finalizeThread();
    nav(`/interview/${encodeURIComponent(sessionId)}/recap`);
  };

  // ── Empty / loading states ────────────────────────────────────────────
  if (prepListQ.isLoading || prepMdQ.isLoading) {
    return <div className="empty"><span className="serif">Loading prep pack…</span></div>;
  }

  if (allQuestions.length === 0) {
    return (
      <div className="card empty" style={{ padding: 28 }}>
        <span className="serif">No prep pack yet for this session.</span>
        Generate one first — it'll produce 13+ likely questions categorized by type, plus a story-bank
        mapping for behavioral questions. Practice will load them here automatically.
        <div style={{ marginTop: 18 }}>
          <button className="btn accent" onClick={onSwitchToPrepTab}>
            <Sparkles size={13} style={{ marginRight: 6, display: 'inline' }} />
            Open Prep tab to generate
          </button>
        </div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div>
        <FilterStrip filter={filter} setFilter={setFilter} counts={counts} total={allQuestions.length} />
        <div className="card empty" style={{ padding: 24, marginTop: 12 }}>
          No {CATEGORY_LABEL[filter as PrepCategory]} questions. Try another filter.
        </div>
      </div>
    );
  }

  const pct = Math.min(100, (elapsed / 120) * 100);

  // ── "Reviewing previous attempt" banner ─────────────────────────────────
  // When the user has graded this session at least once, surface the loop:
  // edit answers → re-grade → see progress. Notes are already preserved
  // across grade runs (notesByQuestion is just keyed by question text), so
  // textareas auto-fill with prior answers; this banner just makes the
  // intent explicit and links straight back to the recap.
  const gradeHistory = state.gradeHistory ?? [];
  const lastGrade = gradeHistory.length > 0
    ? gradeHistory.reduce((a, b) => (a.gradedAt > b.gradedAt ? a : b))
    : null;

  return (
    <div>
      <FilterStrip filter={filter} setFilter={setFilter} counts={counts} total={allQuestions.length} />

      {lastGrade && (
        <div
          className="card"
          style={{
            marginTop: 12,
            padding: '12px 16px',
            background: 'rgba(120, 90, 200, 0.04)',
            borderColor: 'rgba(120, 90, 200, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: 'var(--ink2)' }}>
            <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
              Reviewing your previous attempt
            </span>
            {' · '}
            last grade <span className="num" style={{ color: 'var(--pri)' }}>{lastGrade.overallScore}</span>/100
            {' · '}
            <span style={{ color: 'var(--ink3)' }}>
              {gradeHistory.length} attempt{gradeHistory.length === 1 ? '' : 's'} on record
            </span>
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink3)', flexBasis: '100%' }}>
            Edit any answer below — the next grade adds a new history entry, doesn't overwrite.
          </span>
          <button
            className="btn"
            onClick={() => nav(`/interview/${encodeURIComponent(sessionId)}/recap`)}
            style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            View recap →
          </button>
        </div>
      )}

      <div className="card" style={{ padding: 26, marginTop: 14 }}>
        {/* Header bar — just question position now; record/voice merged below */}
        <div className="tab" style={{ marginBottom: 8 }}>
          Question {idx + 1} of {filtered.length} · {CATEGORY_LABEL[q.category]}
        </div>

        {/* The question, big and serif. Rendered as markdown so models'
            *italic emphasis* and `code` survive — but we restrict to inline
            elements so it stays one visual block, not a paragraph stack. */}
        <div className="serif" style={{ fontSize: 26, lineHeight: 1.25 }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <>{children}</>,
              h1: ({ children }) => <>{children}</>,
              h2: ({ children }) => <>{children}</>,
              h3: ({ children }) => <>{children}</>,
            }}
          >
            {q.text}
          </ReactMarkdown>
        </div>
        {q.storyHint && (
          <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Star size={11} /> Story hint: {q.storyHint}
          </div>
        )}

        {/* ── Merged Record panel (timer + STT) ─────────────────────────── */}
        <div
          style={{
            marginTop: 18,
            padding: '14px 16px',
            background: recording ? 'rgba(220, 60, 60, 0.04)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${recording ? 'rgba(220,60,60,0.3)' : 'var(--line2)'}`,
            borderRadius: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className={recording ? 'btn warn' : 'btn accent'}
              onClick={() => setRecording((v) => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 160 }}
              title={recording ? 'Stop recording — your transcript stays in notes' : (voice.caps.canRecognize ? 'Records your spoken answer (mic + 2-min timer)' : 'Starts the 2-minute timer (no STT in this browser)')}
            >
              {recording ? <Square size={12} fill="currentColor" /> : <Circle size={12} fill="currentColor" />}
              {recording
                ? 'Stop'
                : voice.caps.canRecognize
                  ? 'Start practicing'
                  : 'Start timer'}
            </button>
            <div style={{ flex: 1 }}>
              <Progress value={pct} color={pct >= 90 ? 'var(--warn)' : recording ? 'var(--warn)' : 'var(--accent)'} />
              <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="num">
                  {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')} / 2:00
                </span>
                {recording && voice.caps.canRecognize && (
                  <>
                    <span style={{ color: 'var(--warn)', fontWeight: 500 }}>
                      ● {voice.listening ? 'mic live' : 'starting…'}
                    </span>
                    <span>{segments} segment{segments === 1 ? '' : 's'} captured</span>
                  </>
                )}
                {recording && voice.speaking && (
                  <button
                    className="btn ghost"
                    onClick={voice.stopSpeaking}
                    style={{ fontSize: 10.5, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <VolumeX size={10} /> Hush bot
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Live interim transcript — prominent so the user knows it's working */}
          {recording && voice.caps.canRecognize && (
            <>
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: 'rgba(255,255,255,0.6)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  minHeight: 52,
                  fontFamily: 'Fraunces, var(--cjk-serif), serif',
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: voice.interim ? 'var(--ink)' : 'var(--ink3)',
                  fontStyle: voice.interim ? 'normal' : 'italic',
                }}
              >
                {voice.interim || (voice.listening ? 'Listening — start speaking your answer.' : 'Waiting for mic permission…')}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 4 }}>
                STT language: <span className="num">{voice.lang}</span>
                {' · '}
                Real-time transcription. The text above auto-fills your notes when you pause or click Stop.
              </div>
            </>
          )}

          {/* After Stop: confirmation of what was captured (so user can see
              the transcript landed) + manual rescue if for some reason the
              browser didn't fire a final commit. */}
          {!recording && lastCaptured && voice.caps.canRecognize && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                background: 'rgba(60,150,90,0.06)',
                border: '1px solid rgba(60,150,90,0.3)',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--ink2)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <span style={{ color: 'var(--good)', flexShrink: 0 }}>✓</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>Last captured ({segments} segment{segments === 1 ? '' : 's'} into notes):</div>
                <div style={{ fontStyle: 'italic', color: 'var(--ink3)' }}>"{lastCaptured.length > 200 ? lastCaptured.slice(0, 200) + '…' : lastCaptured}"</div>
              </div>
            </div>
          )}

          {/* Rescue: if voice.interim still has text after stop (e.g. fallback
              timer hasn't fired or browser dropped the final), let user push
              it to notes manually. */}
          {!recording && voice.interim && voice.caps.canRecognize && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 12px',
                background: 'rgba(220,140,40,0.06)',
                border: '1px solid rgba(220,140,40,0.3)',
                borderRadius: 6,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ flex: 1, color: 'var(--ink2)' }}>
                Heard but not yet committed: "{voice.interim.length > 100 ? voice.interim.slice(0, 100) + '…' : voice.interim}"
              </span>
              <button
                className="btn"
                onClick={() => {
                  if (voice.useLastHeard()) {
                    setSegments((n) => n + 1);
                    setLastCaptured(voice.interim);
                  }
                }}
                style={{ fontSize: 11, padding: '3px 10px' }}
              >
                Use this text
              </button>
            </div>
          )}

          {/* Browser-without-STT hint */}
          {!voice.caps.canRecognize && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink3)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={11} />
              Speech-to-text needs Chrome or Edge. Type your answer in the notes field below.
            </div>
          )}

          {/* Voice errors (permission, network, etc) */}
          {voice.error && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 6, fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{voice.error}</span>
            </div>
          )}
        </div>

        {/* Notes textarea (also receives STT final transcripts) */}
        <div className="tab" style={{ margin: '18px 0 6px' }}>Your answer / notes</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={6}
          style={{ width: '100%', padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', resize: 'vertical' }}
          placeholder="Speak or type your STAR+R answer. Get feedback when you're done."
        />

        {/* Actions row — Record button moved to its own panel above */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            className="btn"
            onClick={startAnswer}
            disabled={answerRunner.isRunning}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Draft a STAR+R answer using your CV + position context"
          >
            <Sparkles size={12} />
            {answerRunner.isRunning ? 'Drafting…' : (cleanAnswer ? 'Re-draft answer' : 'Draft STAR+R answer')}
          </button>
          <button
            className="btn"
            onClick={startGrade}
            disabled={gradeRunner.isRunning || !notes.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="LLM critiques your answer — needs notes filled in"
          >
            <Star size={12} />
            {gradeRunner.isRunning ? 'Grading…' : (cleanGrade ? 'Re-grade' : 'Get feedback')}
          </button>
          {/* Multi-turn probe driver — explicit user action, not auto. */}
          <button
            className="btn"
            onClick={triggerProbe}
            disabled={probeRunner.isRunning || !notes.trim() || !!pendingProbe}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Ask the LLM if there's a sharper follow-up to dig deeper"
          >
            <Sparkles size={12} />
            {probeRunner.isRunning
              ? 'Thinking of a probe…'
              : turnIndex === 0
                ? 'Probe deeper →'
                : `Probe further (turn ${turnIndex + 1}) →`}
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={prev} disabled={idx === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ChevronLeft size={12} /> Prev
          </button>
          <button className="btn" onClick={next} disabled={idx >= filtered.length - 1} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Done & next <ChevronRight size={12} />
          </button>
          <button className="btn accent" onClick={finish}>
            Finish → recap
          </button>
        </div>

        {/* Drafted answer */}
        {showAnswer && (cleanAnswer || answerRunner.isRunning) && (
          <div style={{ marginTop: 18, padding: '14px 16px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div className="tab" style={{ flex: 1 }}>Drafted STAR+R answer</div>
              <div style={{ fontSize: 10.5, color: 'var(--ink3)' }}>
                {answerRunner.status}{answerRunner.isRunning ? ' · streaming' : ''}
              </div>
            </div>
            {answerRunner.isRunning && (
              <Progress value={Math.min(95, cleanAnswer.length / 20)} />
            )}
            {displayAnswer && (
              <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', marginTop: 8 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayAnswer}</ReactMarkdown>
              </div>
            )}
            {answerRunner.status === 'done' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn ghost" onClick={() => {
                  setNotes(notes ? `${notes}\n\n${displayAnswer}` : displayAnswer);
                }}>
                  Append to notes
                </button>
                <button className="btn ghost" onClick={() => { answerRunner.dismiss(); setShowAnswer(false); }}>
                  Discard
                </button>
              </div>
            )}
          </div>
        )}

        {/* Feedback */}
        {showFeedback && (cleanGrade || gradeRunner.isRunning) && (
          <div style={{ marginTop: 14, padding: '14px 16px', background: 'rgba(180,140,40,0.04)', border: '1px solid #eddbc1', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div className="tab" style={{ flex: 1 }}>Hiring-manager feedback</div>
              <div style={{ fontSize: 10.5, color: 'var(--ink3)' }}>
                {gradeRunner.status}
              </div>
            </div>
            {gradeRunner.isRunning && (
              <Progress value={Math.min(95, cleanGrade.length / 12)} />
            )}
            {cleanGrade && (
              <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', marginTop: 8 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanGrade}</ReactMarkdown>
              </div>
            )}
            {gradeRunner.status === 'done' && (
              <button className="btn ghost" onClick={() => { gradeRunner.dismiss(); setShowFeedback(false); }} style={{ marginTop: 8 }}>
                Dismiss
              </button>
            )}
          </div>
        )}

        {/* Pending probe banner — shown when LLM came back with a follow-up.
            User decides: continue (commit current Q/A → swap question) or
            stop (dismiss banner; their answer stays in notes for finalize). */}
        {pendingProbe && (
          <div style={{ marginTop: 14, padding: '14px 16px', background: 'rgba(220,140,40,0.08)', border: '1px solid rgba(220,140,40,0.4)', borderRadius: 8 }}>
            <div className="tab" style={{ marginBottom: 8, color: 'var(--warn)' }}>
              Follow-up probe (turn {turnIndex + 2})
            </div>
            <div className="serif" style={{ fontSize: 18, lineHeight: 1.4, color: 'var(--ink)' }}>
              {pendingProbe.probe}
            </div>
            {pendingProbe.reason && (
              <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginTop: 6, fontStyle: 'italic' }}>
                Why: {pendingProbe.reason}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                className="btn accent"
                onClick={acceptProbe}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                Continue with this probe →
              </button>
              <button
                className="btn ghost"
                onClick={rejectProbeAndAdvance}
                title="Dismiss this probe — your answer stays as-is. Click Done & next when ready."
              >
                Stop probing
              </button>
            </div>
          </div>
        )}
        {/* "Strong answer — no probe needed" affirmation (only when LLM
            decided move_on:true; lets user know the loop is done for now). */}
        {probeRunner.status === 'done' && probeResult?.move_on && !pendingProbe && (
          <div style={{ marginTop: 14, padding: '8px 14px', background: 'rgba(60,150,90,0.06)', border: '1px solid rgba(60,150,90,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--good)' }}>
            ✓ Strong, complete answer — no probe needed.
            {probeResult.reason && <span style={{ color: 'var(--ink3)', marginLeft: 6 }}>({probeResult.reason})</span>}
            {' '}Click <b>Done & next</b> when ready.
          </div>
        )}

        {/* Conversation history within this anchor (shows after first probe
            is accepted). Lets user scroll back through what they've said
            so far in this thread. Compact, read-only. */}
        {threadTurns.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--line2)' }}>
            <div className="tab" style={{ marginBottom: 8 }}>
              Conversation so far ({threadTurns.length} turn{threadTurns.length === 1 ? '' : 's'} in this thread)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
              {threadTurns.map((t, i) => (
                <div key={i} style={{ borderLeft: '2px solid var(--line)', paddingLeft: 10 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginBottom: 2 }}>
                    {i === 0 ? 'Anchor' : `Probe ${i}`}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink2)', marginBottom: 4 }}>
                    {t.question}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {t.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session progress */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--line2)', fontSize: 11, color: 'var(--ink3)' }}>
          {(() => {
            const anchorsAnswered = new Set(state.qaPairs.map((p) => p.anchor || p.question)).size;
            return `${anchorsAnswered} of ${filtered.length} prep questions covered`;
          })()}
          {state.qaPairs.length >= 1 && (
            <>
              {' · '}
              <button className="btn ghost" onClick={finish} style={{ fontSize: 11, padding: '1px 6px' }}>
                Finish → recap
              </button>
              {' '}to grade the whole session
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Filter strip (shared little component) ───────────────────────────────
function FilterStrip({
  filter, setFilter, counts, total,
}: {
  filter: PrepCategory | 'all';
  setFilter: (f: PrepCategory | 'all') => void;
  counts: Record<PrepCategory, number>;
  total: number;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <Chip
        variant={filter === 'all' ? 'primary' : 'default'}
        onClick={() => setFilter('all')}
      >
        All ({total})
      </Chip>
      {CATEGORY_ORDER.map((cat) => (
        counts[cat] > 0 && (
          <Chip
            key={cat}
            variant={filter === cat ? 'primary' : 'default'}
            onClick={() => setFilter(cat)}
          >
            {CATEGORY_LABEL[cat]} ({counts[cat]})
          </Chip>
        )
      ))}
    </div>
  );
}
